import type { RouteObject } from 'react-router';
import { configureModule } from './configure';
import { monitorModule } from './monitor';
import type { ModuleDefinition, ModuleNavItem } from './types';

export type { ModuleDefinition, ModuleNavItem, NavBadge, TopBarAction } from './types';

/**
 * The registry. Both the sidebar and the router are derived from this array, so a new area
 * of the dashboard is one module file plus one entry here.
 *
 * @since 1.0.0
 */
export const MODULES: ModuleDefinition[] = [monitorModule, configureModule];

/**
 * Every module's routes, flattened for the router.
 *
 * @since 1.0.0
 */
export const MODULE_ROUTES: RouteObject[] = MODULES.flatMap((definition) => definition.routes);

/**
 * Every module's nav items, flattened for lookups.
 *
 * @since 1.0.0
 */
export const NAV_ITEMS: ModuleNavItem[] = MODULES.flatMap((definition) => definition.items);

/**
 * Finds the nav item that owns a pathname, preferring an exact match and otherwise the
 * longest path the location sits underneath.
 *
 * @since 1.0.0
 * @param pathname - the current location pathname
 * @returns the owning nav item, or null when nothing matches
 */
export function findNavItem(pathname: string): ModuleNavItem | null {
    const exact = NAV_ITEMS.find((item) => item.path === pathname);

    if (exact) {
        return exact;
    }

    const nested = NAV_ITEMS.filter((item) => item.path !== '/' && pathname.startsWith(`${item.path}/`)).sort(
        (first, second) => second.path.length - first.path.length,
    );

    return nested[0] ?? null;
}
